import { Geo } from "@rogoag/geo";
import type { Job } from "../types";

/** MC's "Show stats" aggregates over a set of jobs (SchedulingMap.tsx displayStats), with
 *  two MC defects fixed: totals are not rounded before averaging (rounding is display-only)
 *  and jobs with an empty group value bucket under UNSET_GROUP_KEY instead of vanishing, so
 *  group rows sum to the top line. Averages keep MC's rule: divide by the count of fields
 *  with a nonzero value, so a field without data doesn't drag the average down. */
export interface JobAreaStats {
	fieldCount: number;
	totalBoundaryAcres: number;
	totalOpsAcres: number;
	totalSamples: number;
	avgAcresPerField: number;
	avgSamplesPerField: number;
}

/** MC's Group Statistics keys: Test Package / Grower / Client / SubmitterName. */
export type JobStatsGroupBy = "testPackage" | "growerName" | "companyName" | "submitterName";

export const UNSET_GROUP_KEY = "(unset)";

export interface JobGroupStats {
	key: string;
	stats: JobAreaStats;
}

/** Boundary acres for one job: measured from the loaded boundary geometry when present
 *  (matching the shift cards' sumAcresForJobIds), else the "Boundary Acres" field.
 *  Malformed or empty geometry falls back to the field. */
export function jobBoundaryAcres(job: Job): number {
	const fc = job.files?.boundaryGeoJSON;
	if (fc) {
		try {
			const acres = Geo.area(fc, "acres");
			if (acres > 0) return acres;
		} catch {
			// Malformed geometry — fall back to the field.
		}
	}
	return job.boundaryAcres || 0;
}

/** MC's shift-acres rule (DayShift.tsx, Drew 2025-09-29): "Ops Acre Eq." over "Boundary
 *  Acres", boundary as fallback — including when ops acres compute to 0 (MC's truthy check).
 *  Purely field-based (NOT jobBoundaryAcres, which is geometry-first): a shift card's total
 *  must equal MC's field-sum and not drift as boundary geometry loads in. */
export function jobOpsOrBoundaryAcres(job: Job): number {
	return job.opsAcres ? job.opsAcres : job.boundaryAcres || 0;
}

/** Shift-card total: ops-else-boundary acres summed over the shift's jobs, each counted once. */
export function sumOpsOrBoundaryAcres(jobs: readonly Job[]): number {
	let acres = 0;
	for (const job of dedupeById(jobs)) acres += jobOpsOrBoundaryAcres(job);
	return acres;
}

function dedupeById(jobs: readonly Job[]): Job[] {
	const seen = new Set<string>();
	const unique: Job[] = [];
	for (const job of jobs) {
		if (seen.has(job.id)) continue;
		seen.add(job.id);
		unique.push(job);
	}
	return unique;
}

export function summarizeJobs(jobs: readonly Job[]): JobAreaStats {
	const unique = dedupeById(jobs);
	let totalBoundaryAcres = 0;
	let totalOpsAcres = 0;
	let totalSamples = 0;
	let validAcresCount = 0;
	let validSamplesCount = 0;
	for (const job of unique) {
		const acres = jobBoundaryAcres(job);
		const samples = job.numberOfSamples ?? 0;
		totalBoundaryAcres += acres;
		totalOpsAcres += job.opsAcres ?? 0;
		totalSamples += samples;
		if (acres) validAcresCount++;
		if (samples) validSamplesCount++;
	}
	return {
		fieldCount: unique.length,
		totalBoundaryAcres,
		totalOpsAcres,
		totalSamples,
		avgAcresPerField: validAcresCount > 0 ? totalBoundaryAcres / validAcresCount : 0,
		avgSamplesPerField: validSamplesCount > 0 ? totalSamples / validSamplesCount : 0,
	};
}

export function groupJobStats(jobs: readonly Job[], groupBy: JobStatsGroupBy): JobGroupStats[] {
	const buckets = new Map<string, Job[]>();
	for (const job of dedupeById(jobs)) {
		const key = job[groupBy] || UNSET_GROUP_KEY;
		const bucket = buckets.get(key);
		if (bucket) bucket.push(job);
		else buckets.set(key, [job]);
	}
	const groups = [...buckets.entries()].map(([key, bucket]) => ({ key, stats: summarizeJobs(bucket) }));
	// First-seen order, with the unset bucket pinned last.
	return [...groups.filter((g) => g.key !== UNSET_GROUP_KEY), ...groups.filter((g) => g.key === UNSET_GROUP_KEY)];
}
